import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import { useAuth } from '../context/AuthContext';
import BlockButton from '../components/BlockButton';
import AdminButtons from '../components/AdminButtons';
import ReportButton from '../components/ReportButton';
import config from '../config/config';

import { ArrowLeft, MapPin, Calendar, Heart, MessageCircle, Clock } from 'lucide-react';

const UserProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [error, setError] = useState(null);

  // Forzar scroll hacia arriba cuando se carga la página
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const loadUser = async () => {
      try {
        setLoading(true);
        const userData = await apiService.getUserById(id);
        
        // Transformar datos del backend al formato del frontend
        const transformedUser = {
          id: userData.id,
          username: userData.username,
          email: userData.email,
          name: userData.firstName || 'Usuario',
          age: userData.age,
          gender: userData.sex === 'male' ? 'Hombre' : 'Mujer',
          state: userData.location || 'Ciudad de México',
          description: userData.bio || '',
          hasPhoto: !!(userData.profilePicture || userData.profile_picture),
          photoUrl: userData.profilePicture || userData.profile_picture || null,
          isOnline: userData.isOnline === 1 || userData.isOnline === true,
          lastSeen: userData.lastLogin || userData.last_login,
          matchSex: userData.matchSex || userData.match_sex || [],
          // Nuevos campos
          estadoCivil: userData.estadoCivil,
          sexoBusca: userData.sexoBusca,
          descripcionFisica: userData.descripcionFisica,
          educacion: userData.educacion,
          ocupacion: userData.ocupacion,
          tipoPersonaBusca: userData.tipoPersonaBusca
        };
        
                 setUser(transformedUser);
         setError(null);
    } catch (err) {
         const errorMsg = 'No se pudo cargar el perfil del usuario';
         setError(errorMsg);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadUser();
    }
  }, [id]);

  const formatLastSeen = (dateString) => {
    if (!dateString) return 'Nunca';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Ahora mismo';
    if (diffInMinutes < 60) return `Hace ${diffInMinutes} minutos`;
    if (diffInMinutes < 1440) return `Hace ${Math.floor(diffInMinutes / 60)} horas`;
    return `Hace ${Math.floor(diffInMinutes / 1440)} días`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="card text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-text">Cargando perfil...</h2>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="card text-center">
          <h2 className="text-2xl font-bold text-text mb-2">Usuario no encontrado</h2>
          <p className="text-gray-600 mb-4">{error || 'El perfil que buscas no existe.'}</p>
                     <Link to="/buscar-pareja" className="btn-primary hover:scale-105 active:scale-95 transition-all duration-200 shadow-md hover:shadow-lg">
             Volver a buscar
           </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Notificación */}
        {notification && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
            {notification}
          </div>
        )}
        
        {/* Botón de regreso */}
        <div className="mb-6">
          <Link
            to="/buscar-pareja"
            className="inline-flex items-center space-x-2 text-gray-600 hover:text-primary transition-all duration-200 hover:scale-105 active:scale-95"
          >
            <ArrowLeft className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-1" />
            <span>Volver a la búsqueda</span>
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Columna izquierda - Foto y info básica */}
          <div className="lg:col-span-1">
            <div className="card">
              <div className="text-center">
                <div className="relative inline-block mb-6">
                  <img
                    src={user.photoUrl || config.getDefaultAvatar(user.gender)}
                    alt={user.name}
                    className="w-48 h-48 sm:w-56 sm:h-56 md:w-64 md:h-64 rounded-full object-cover mx-auto border-4 border-white shadow-lg"
                    onError={(e) => {
                      e.target.src = config.getDefaultAvatar(user.gender);
                    }}
                  />
                  <div className="absolute -bottom-2 -right-2">
                    <div className={`w-6 h-6 rounded-full border-4 border-white ${
                      user.isOnline ? 'bg-green-500' : 'bg-gray-400'
                    }`}></div>
                  </div>
                </div>
                
                <h1 className="text-3xl font-bold text-text mb-1">{user.name}</h1>
                <p className="text-gray-600">@{user.username}</p>
                {user.email && (
                  <p className="text-gray-500 text-sm mt-2">
                    {user.email}
                  </p>
                )}
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-center justify-center space-x-2 text-gray-600">
                    <Calendar className="h-4 w-4" />
                    <span>{user.age} años</span>
                  </div>
                  <div className="flex items-center justify-center space-x-2 text-gray-600">
                    <MapPin className="h-4 w-4" />
                    <span>{user.state}{user.municipio && `, ${user.municipio}`}</span>
                  </div>
                  <div className="flex items-center justify-center space-x-2 text-gray-600">
                    <Heart className="h-4 w-4" />
                    <span>{user.gender}</span>
                  </div>
                  <div className="flex items-center justify-center space-x-2 text-gray-600">
                    <Clock className="h-4 w-4" />
                    <span>
                      {user.isOnline ? 'En línea' : `Visto ${formatLastSeen(user.lastSeen)}`}
                    </span>
                  </div>
                </div>

                <div className="space-y-3">
                  {currentUser && currentUser.id !== parseInt(id) ? (
                    <>
                      <button 
                        onClick={() => {
                          setTimeout(() => {
                            navigate('/mensajes', { state: { startChatWith: user } });
                          }, 500);
                        }}
                        className="w-full btn-primary flex items-center justify-center space-x-2 hover:scale-105 active:scale-95 transition-all duration-200 shadow-md hover:shadow-lg"
                      >
                        <MessageCircle className="h-4 w-4 transition-transform duration-200 group-hover:rotate-12" />
                        <span>Enviar Mensaje</span>
                      </button>
                      
                      {/* Botón de "Me Gusta" temporalmente oculto
                      <button 
                        onClick={() => {
                          // TODO: Implementar funcionalidad de "Me Gusta"
                        }}
                        className="w-full btn-outline flex items-center justify-center space-x-2 hover:bg-pink-50 hover:border-pink-300 hover:text-pink-600 transition-all duration-200 hover:scale-105 active:scale-95"
                      >
                        <Heart className="h-4 w-4 transition-transform duration-200 group-hover:scale-110" />
                        <span>Me Gusta</span>
                      </button>
                      */}
                      
                      {/* Botones de admin o bloqueo */}
                      {config.isAdmin(currentUser) ? (
                        <AdminButtons 
                          targetUser={user}
                          onUserDeleted={(userId) => {
                            // Si el usuario fue eliminado, redirigir a búsqueda
                            navigate('/buscar-pareja');
                          }}
                          onUserUpdated={(userId, userData) => {
                            // Recargar el perfil con los datos actualizados
                            window.location.reload();
                          }}
                          className="w-full"
                          size="md"
                        />
                      ) : (
                        <>
                          <BlockButton 
                            targetUser={user}
                            onBlockChange={(userId, isBlocked) => {
                              if (isBlocked) {
                                // Si el usuario fue bloqueado, redirigir a búsqueda
                                navigate('/buscar-pareja');
                              }
                            }}
                            className="w-full"
                            size="md"
                          />
                          <ReportButton 
                            targetUser={user}
                            onReportSent={(message) => {
                              setNotification(message);
                              setTimeout(() => setNotification(null), 5000);
                            }}
                          />
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      {!currentUser ? (
                        <button
                          onClick={() => navigate('/mi-cuenta')}
                          className="w-full btn-primary flex items-center justify-center space-x-2 hover:scale-105 active:scale-95 transition-all duration-200 shadow-md hover:shadow-lg"
                        >
                          <MessageCircle className="h-4 w-4 transition-transform duration-200 group-hover:rotate-12" />
                          <span>Iniciar sesión para enviar mensajes</span>
                        </button>
                      ) : (
                        <div className="w-full text-center text-gray-500 py-3">
                          No puedes enviarte mensajes a ti mismo
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Columna derecha - Información detallada */}
          <div className="lg:col-span-2">
            <div className="space-y-6">
              {/* Sobre mí */}
              <div className="card">
                <h2 className="text-xl font-semibold text-text mb-4">Sobre mí</h2>
                <p className="text-gray-700 leading-relaxed">
                  {user.description || "Este usuario aún no ha agregado una descripción."}
                </p>
              </div>

              {/* Información Personal */}
              <div className="card">
                <h2 className="text-xl font-semibold text-text mb-4">Información Personal</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {user.estadoCivil && (
                    <div>
                      <label className="form-label">Estado Civil</label>
                      <p className="text-gray-700">{user.estadoCivil}</p>
                    </div>
                  )}
                  {user.educacion && (
                    <div>
                      <label className="form-label">Educación</label>
                      <p className="text-gray-700">{user.educacion}</p>
                    </div>
                  )}
                  {user.ocupacion && (
                    <div>
                      <label className="form-label">Ocupación</label>
                      <p className="text-gray-700">{user.ocupacion}</p>
                    </div>
                  )}
                  {user.descripcionFisica && (
                    <div className="md:col-span-2">
                      <label className="form-label">Descripción Física</label>
                      <p className="text-gray-700">{user.descripcionFisica}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Preferencias de Búsqueda */}
              <div className="card">
                <h2 className="text-xl font-semibold text-text mb-4">Lo que Busco</h2>
                {user.sexoBusca && (
                  <div className="mb-4">
                    <label className="form-label">Busca</label>
                    <p className="text-gray-700">{user.sexoBusca}</p>
                  </div>
                )}
                {user.tipoPersonaBusca && (
                  <div>
                    <label className="form-label">Tipo de Persona</label>
                    <p className="text-gray-700">{user.tipoPersonaBusca}</p>
                  </div>
                )}
              </div>

              {/* Botón de contacto */}
              {currentUser && currentUser.id !== parseInt(id) && (
                <div className="card">
                  <button 
                    onClick={() => {
                      setTimeout(() => {
                        navigate('/mensajes', { state: { startChatWith: user } });
                      }, 500);
                    }}
                    className="w-full btn-primary flex items-center justify-center space-x-2 hover:scale-105 active:scale-95 transition-all duration-200 shadow-md hover:shadow-lg"
                  >
                    <MessageCircle className="h-4 w-4 transition-transform duration-200 group-hover:rotate-12" />
                    <span>Mándame mensaje</span>
                  </button>
                </div>
              )}

              {/* Preferencias de búsqueda */}
              {user.matchSex && user.matchSex.length > 0 && (
                <div className="card">
                  <h2 className="text-xl font-semibold text-text mb-4">Busca</h2>
                  <div className="flex flex-wrap gap-2">
                    {user.matchSex.map((preference, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-sm font-medium"
                      >
                        {preference === 'male' ? 'Hombres' : 
                         preference === 'female' ? 'Mujeres' : 
                         preference === 'couple' ? 'Parejas' : preference}
                      </span>
                    ))}
                    {user.matchSex.length === 2 && user.matchSex.includes('male') && user.matchSex.includes('female') && (
                      <span className="px-3 py-1 bg-purple-50 text-purple-600 rounded-full text-sm font-medium">
                        Ambos
                      </span>
                    )}
                  </div>
                </div>
              )}



            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
